import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Users, Plus, Search, Phone, Mail, Building2, X, ArrowLeft, Pencil,
  ShoppingCart, DollarSign, Clock, CheckCircle2, Loader2, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

import { PortalAccessCard, PricingPanel, CustomerActivity } from './CommercialPanels';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const STATUS_META = {
  pending: { label: 'Pending', color: '#F59E0B' },
  processing: { label: 'Processing', color: '#3B82F6' },
  ready_to_ship: { label: 'Ready to Ship', color: '#6366F1' },
  shipped: { label: 'Shipped', color: '#8B5CF6' },
  in_transit: { label: 'In Transit', color: '#8B5CF6' },
  delivered: { label: 'Delivered', color: '#10B981' },
  completed: { label: 'Completed', color: '#10B981' },
  cancelled: { label: 'Cancelled', color: '#EF4444' },
  paid: { label: 'Paid', color: '#10B981' },
};
const stMeta = (s) => STATUS_META[s] || { label: (s || '').charAt(0).toUpperCase() + (s || '').slice(1), color: '#64748B' };

const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1.5">{label}{required && <span className="text-[#C6A15B]"> *</span>}</label>
    {children}
  </div>
);
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none transition-colors";

// ---------------- Add Client Drawer ----------------
const AddClientDrawer = ({ open, onClose, onCreated }) => {
  const blank = { company_name: '', full_name: '', email: '', phone: '', address: '', username: '', role: 'installation_center' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(blank); /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.full_name || !form.email || !form.username) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSaving(true);
    try {
      // Ownership is assigned server-side from the authenticated rep; never sent here.
      // No password is set at creation — the client sets one via a secure portal invitation.
      await axios.post(`${API_BASE_URL}/api/users`, {
        username: form.username.trim(),
        email: form.email.trim(),
        password: Math.random().toString(36).slice(2) + 'Aa1',
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim(),
        role: form.role,
        phone: form.phone,
        address: form.address,
      });
      toast.success('Client created. You can invite them to the portal from their profile.');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create client.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="add-client-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-[#0C0E11] border-l border-white/[0.08] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-[#0C0E11]/90 backdrop-blur-xl border-b border-white/[0.06] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#C6A15B]" />
            <h3 className="text-white font-semibold">New Client</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="add-client-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-xs text-slate-500 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2">
            This client will be automatically assigned to you. No login is created yet — you can send them a secure portal invitation from their profile once setup is complete.
          </p>
          <Field label="Company Name" required>
            <input className={inputCls} value={form.company_name} onChange={set('company_name')} data-testid="client-company-input" />
          </Field>
          <Field label="Primary Contact" required>
            <input className={inputCls} value={form.full_name} onChange={set('full_name')} data-testid="client-contact-input" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email" required>
              <input type="email" className={inputCls} value={form.email} onChange={set('email')} data-testid="client-email-input" />
            </Field>
            <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set('phone')} data-testid="client-phone-input" /></Field>
          </div>
          <Field label="Address"><input className={inputCls} value={form.address} onChange={set('address')} data-testid="client-address-input" /></Field>
          <Field label="Account Type">
            <div className="flex items-center h-10 px-3 rounded-lg bg-black/30 border border-white/10 text-slate-300 text-sm" data-testid="client-role-fixed">
              Installation Center
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Elite Partner accounts are created and managed by TC Admin.</p>
          </Field>
          <Field label="Username" required>
            <input className={inputCls} value={form.username} onChange={set('username')} data-testid="client-username-input" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-white/[0.05] hover:bg-white/[0.1] text-slate-200 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60" data-testid="add-client-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Create Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------- Client Profile ----------------
const EditClientForm = ({ client, onCancel, onSaved }) => {
  const [form, setForm] = useState({
    full_name: client.full_name || '', company_name: client.company_name || '',
    email: client.email || '', phone: client.phone || '', address: client.address || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/sales-rep/clients/${client.user_id}`, form);
      toast.success('Client updated.');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed.');
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="edit-client-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Company Name"><input className={inputCls} value={form.company_name} onChange={set('company_name')} data-testid="edit-company-input" /></Field>
        <Field label="Primary Contact"><input className={inputCls} value={form.full_name} onChange={set('full_name')} data-testid="edit-contact-input" /></Field>
        <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={set('email')} data-testid="edit-email-input" /></Field>
        <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set('phone')} data-testid="edit-phone-input" /></Field>
      </div>
      <Field label="Address"><input className={inputCls} value={form.address} onChange={set('address')} data-testid="edit-address-input" /></Field>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className="flex-1 bg-white/[0.05] hover:bg-white/[0.1] text-slate-200 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">Cancel</button>
        <button type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60" data-testid="edit-client-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save Changes
        </button>
      </div>
    </form>
  );
};

const StatTile = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-black/30 border border-white/[0.06] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className={`w-4 h-4 ${accent}`} /><span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-white text-xl font-semibold">{value}</p>
  </div>
);

const ClientProfile = ({ clientId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/sales-rep/clients/${clientId}`);
      setData(res.data);
    } catch (e) {
      toast.error('Could not load client.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const c = data?.client;
  const s = data?.statistics || {};

  return (
    <div className="space-y-6" data-testid="client-profile">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="client-profile-back">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </button>

      {loading ? (
        <div className="space-y-4"><Skeleton className="h-28" /><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
      ) : !c ? (
        <p className="text-slate-500">Client not found.</p>
      ) : (
        <>
          {/* Header card */}
          <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-4 min-w-0">
                <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A15B] to-[#8a6f3a] text-black font-bold flex items-center justify-center shrink-0">
                  {(c.company_name || c.full_name || 'C').slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-white truncate">{c.company_name || c.full_name}</h2>
                  <p className="text-slate-400 text-sm">{c.full_name}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-[#C6A15B]"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-[#C6A15B]"><Phone className="w-3.5 h-3.5" />{c.phone}</a>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${c.is_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
                {!editing && (
                  <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-xs bg-white/[0.05] hover:bg-white/[0.1] text-white px-3 py-1.5 rounded-lg transition-colors" data-testid="edit-client-btn">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
            </div>
          </div>

          {editing ? (
            <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-4">Edit Client Information</h3>
              <EditClientForm client={c} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
            </div>
          ) : (
            <>
              {/* Sales summary */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile icon={ShoppingCart} label="Total Orders" value={s.total_orders ?? 0} accent="text-blue-400" />
                <StatTile icon={DollarSign} label="Total Sales" value={money(s.total_sales)} accent="text-[#C6A15B]" />
                <StatTile icon={Clock} label="Open Orders" value={s.open_orders ?? 0} accent="text-amber-400" />
                <StatTile icon={CheckCircle2} label="Last Order" value={fmtDate(s.last_order)} accent="text-emerald-400" />
              </div>

              {/* Recent orders */}
              <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">Recent Orders</h3>
                {data.recent_orders?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-slate-500 text-xs border-b border-white/[0.06]">
                          <th className="text-left font-medium py-2">Order #</th>
                          <th className="text-left font-medium py-2 hidden sm:table-cell">Date</th>
                          <th className="text-right font-medium py-2">Amount</th>
                          <th className="text-right font-medium py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recent_orders.map((o) => {
                          const m = stMeta(o.status);
                          return (
                            <tr key={o.order_id} className="border-b border-white/[0.04]">
                              <td className="py-2.5 font-mono text-[#C6A15B]">{o.order_number}</td>
                              <td className="py-2.5 text-slate-500 hidden sm:table-cell">{fmtDate(o.date)}</td>
                              <td className="py-2.5 text-right text-white font-medium">{money(o.total)}</td>
                              <td className="py-2.5 text-right"><span className="inline-block text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-slate-500 text-sm py-6 text-center">No orders yet for this client.</p>}
              </div>

              {/* Account information */}
              <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">Account Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><p className="text-slate-500 text-xs mb-0.5">Address</p><p className="text-slate-200">{c.address || '—'}</p></div>
                  <div><p className="text-slate-500 text-xs mb-0.5">Member Since</p><p className="text-slate-200">{fmtDate(c.created_at)}</p></div>
                  <div><p className="text-slate-500 text-xs mb-0.5">Managed By</p><p className="text-slate-200">Sales Representative</p></div>
                  <div><p className="text-slate-500 text-xs mb-0.5">Assigned Representative</p><p className="text-slate-200">{c.assigned_salesperson_name || 'You'}</p></div>
                </div>
                <p className="text-[11px] text-slate-600 mt-3">Account ownership is managed by TC Administration and cannot be changed here.</p>
              </div>

              <PortalAccessCard customerId={c.user_id} />
              <PricingPanel customerId={c.user_id} />
              <CustomerActivity customerId={c.user_id} />
            </>
          )}
        </>
      )}
    </div>
  );
};

// ---------------- My Clients (list) ----------------
export const MyClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/sales-rep/clients`);
      setClients(res.data.clients || []);
    } catch (e) {
      toast.error('Could not load your clients.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = clients;
    if (statusFilter === 'active') list = list.filter((c) => c.is_active);
    if (statusFilter === 'inactive') list = list.filter((c) => !c.is_active);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((c) =>
      (c.company_name || '').toLowerCase().includes(s) ||
      (c.full_name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s));
    return list;
  }, [clients, q, statusFilter]);

  if (selectedId) {
    return <ClientProfile clientId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="my-clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-white">My Clients</h2>
          <p className="text-slate-500 text-sm mt-0.5">{clients.length} account{clients.length === 1 ? '' : 's'} assigned to you</p>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-client-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl transition-colors">
          <Plus className="w-4 h-4" /> Add Client
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by company, contact or email…"
            className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none"
            data-testid="clients-search" />
        </div>
        <div className="flex gap-1 bg-black/30 rounded-lg p-0.5 self-start">
          {[{ k: 'all', l: 'All' }, { k: 'active', l: 'Active' }, { k: 'inactive', l: 'Inactive' }].map((f) => (
            <button key={f.k} onClick={() => setStatusFilter(f.k)} data-testid={`clients-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${statusFilter === f.k ? 'bg-[#C6A15B] text-black font-medium' : 'text-slate-400 hover:text-white'}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-[#14171B] border border-white/[0.06] rounded-2xl" data-testid="clients-empty">
          <Users className="w-10 h-10 text-slate-600 mb-3" />
          <p className="text-slate-200 font-medium">No clients assigned yet.</p>
          <p className="text-slate-500 text-sm mt-1 mb-4">Add your first client to start creating orders.</p>
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl transition-colors" data-testid="clients-empty-add">
            <Plus className="w-4 h-4" /> Add Client
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-slate-500 text-sm py-10 text-center">No clients match your search.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-[#14171B] border border-white/[0.06] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs border-b border-white/[0.06] bg-black/20">
                  <th className="text-left font-medium px-5 py-3">Client</th>
                  <th className="text-left font-medium px-5 py-3">Contact</th>
                  <th className="text-right font-medium px-5 py-3">Orders</th>
                  <th className="text-right font-medium px-5 py-3">Sales</th>
                  <th className="text-left font-medium px-5 py-3">Last Order</th>
                  <th className="text-center font-medium px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.user_id} onClick={() => setSelectedId(c.user_id)} data-testid={`client-row-${c.user_id}`}
                    className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-white/[0.05] text-slate-300 text-xs font-semibold flex items-center justify-center shrink-0">
                          {(c.company_name || c.full_name || 'C').slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="text-slate-100 font-medium truncate">{c.company_name || c.full_name}</p>
                          <p className="text-slate-500 text-xs truncate">{c.full_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-slate-300 text-xs truncate max-w-[200px]">{c.email || '—'}</p>
                      <p className="text-slate-500 text-xs">{c.phone || '—'}</p>
                    </td>
                    <td className="px-5 py-3 text-right text-slate-200">{c.orders}</td>
                    <td className="px-5 py-3 text-right text-white font-medium">{money(c.sales)}</td>
                    <td className="px-5 py-3 text-slate-400">{fmtDate(c.last_order)}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <button key={c.user_id} onClick={() => setSelectedId(c.user_id)} data-testid={`client-card-${c.user_id}`}
                className="w-full text-left bg-[#14171B] border border-white/[0.06] rounded-xl p-4 hover:border-[#C6A15B]/40 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-9 h-9 rounded-lg bg-white/[0.05] text-slate-300 text-xs font-semibold flex items-center justify-center shrink-0">
                      {(c.company_name || c.full_name || 'C').slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-slate-100 font-medium truncate">{c.company_name || c.full_name}</p>
                      <p className="text-slate-500 text-xs truncate">{c.full_name}</p>
                    </div>
                  </div>
                  <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs">
                  <span className="text-slate-400">{c.orders} orders · <span className="text-white font-medium">{money(c.sales)}</span></span>
                  <div className="flex items-center gap-3">
                    {c.phone && <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="text-[#C6A15B]"><Phone className="w-4 h-4" /></a>}
                    {c.email && <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-[#C6A15B]"><Mail className="w-4 h-4" /></a>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <AddClientDrawer open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
    </div>
  );
};

export default MyClients;
